// Over-the-air web-payload updates for the native builds (CapAwesome Live
// Update, self-hosted against this app's own server).
//
// Kept separate from native.js so its one non-negotiable rule is reviewable in
// one place: NEVER INTERRUPT THE USER. Every path fails soft; on the web, and
// anywhere the plugin is absent, the whole module is a no-op.

import { API_BASE, isNative, platform, plugin } from './native.js';
import { APP_SLUG } from '../../app-config.js';

const LU = () => plugin('LiveUpdate');

/**
 * Tell the native side this bundle booted successfully.
 *
 * Non-negotiable: capacitor.config.json sets a readyTimeout, and failing to
 * call this before it expires rolls the app back to the binary's bundle. Call
 * it as early as the app is genuinely usable.
 */
export async function markReady() {
  const p = LU();
  if (!p?.ready) return false;
  try { await p.ready(); return true; } catch { return false; }
}

/**
 * The channel this binary belongs to, read back FROM NATIVE.
 *
 * Never re-derive this in JS. The value encodes staging-vs-production, which is
 * a build-time fact (an xcconfig setting on iOS, a resValue on Android) that JS
 * has no way to know — guessing it is how a staging bundle reaches production
 * users.
 */
export async function currentChannel() {
  const p = LU();
  if (!p?.getChannel) return null;
  try { return (await p.getChannel())?.channel ?? null; } catch { return null; }
}

export async function currentBundleId() {
  const p = LU();
  if (!p?.getCurrentBundle) return null;
  try { return (await p.getCurrentBundle())?.bundleId ?? null; } catch { return null; }
}

/**
 * The bundle already downloaded and queued for the next launch.
 *
 * Asked as well as `currentBundleId`, because they are different questions and
 * only checking the first one costs a download on every resume. A staged bundle
 * is not the RUNNING bundle, so `getCurrentBundle()` keeps reporting the old id
 * for as long as the reload is deferred — and the reload is deferred exactly
 * when the person is busy, which is exactly when they are using data. Every
 * foreground re-downloaded the same megabytes and re-staged them.
 */
export async function stagedBundleId() {
  const p = LU();
  if (!p?.getNextBundle) return null;
  try { return (await p.getNextBundle())?.bundleId ?? null; } catch { return null; }
}

/**
 * Look for a newer bundle and stage it. Does not reload — see applyIfIdle.
 * Returns the staged bundle id, or null when there is nothing new.
 */
export async function checkForUpdate() {
  const p = LU();
  if (!p?.downloadBundle) return null;
  const channel = await currentChannel();
  if (!channel) return null;

  let manifest;
  try {
    const res = await fetch(`${API_BASE}/api/live-update/manifest/${encodeURIComponent(channel)}`, {
      cache: 'no-store',
    });
    if (res.status === 404) return null;   // nothing published on this channel yet
    if (!res.ok) return null;
    manifest = await res.json();
  } catch { return null; }

  if (!manifest?.bundleId || !manifest?.url) return null;
  // Refuse a manifest that belongs to a different appfactory app. This is how
  // a mis-pointed PUBLIC_ORIGIN (or a shared host serving the wrong sibling)
  // used to silently turn one installed app into another after an OTA.
  if (manifest.appSlug !== APP_SLUG) return null;
  if (manifest.bundleId === (await currentBundleId())) return null;
  // Already downloaded and waiting for a safe moment. Return it so the caller
  // can retry the reload, but do not fetch it again.
  if (manifest.bundleId === (await stagedBundleId())) return manifest.bundleId;

  try {
    // The checksum goes to the plugin so verification happens NATIVELY, against
    // the bytes actually written to disk.
    await p.downloadBundle({
      url: manifest.url,
      bundleId: manifest.bundleId,
      checksum: manifest.checksum,
    });
    await p.setNextBundle({ bundleId: manifest.bundleId });
    return manifest.bundleId;
  } catch { return null; }
}

/**
 * Reload into the staged bundle, but only when the caller says it is safe.
 * The predicate is the entire safety mechanism: an update that interrupts
 * someone mid-task is worse than an update that waits.
 */
export async function applyIfIdle(isSafe = () => true) {
  const p = LU();
  if (!p?.reload) return false;
  if (!isSafe()) return false;
  try { await p.reload(); return true; } catch { return false; }
}

/**
 * The whole lifecycle. Runs at boot AND on resume — resume is far more common
 * than a cold start for an installed app, so checking only at launch means most
 * users never see an update.
 */
/**
 * Subscribe this binary to the channel its own build number names.
 *
 * WITHOUT THIS, OTA IS A NO-OP AND LOOKS LIKE A SUCCESS. `getChannel()` returns
 * null when nothing ever set one, `checkForUpdate()` bails on its second line,
 * and no bundle is ever fetched - while `appfactory publish` uploads happily,
 * the manifest serves 200 and the bundle downloads in full to anyone who asks.
 * Every signal outside the app says the update shipped. The app was never
 * listening. Three apps have now shipped in that state.
 *
 * WHY THE BUILD NUMBER. Channels are per build: `appfactory publish ios`
 * resolves `production-ios-<build>` from the latest TestFlight build, so the
 * app has to ask for the same one. `App.getInfo().build` is that number, read
 * from the binary at runtime. It cannot be hardcoded or baked in at sync time
 * without going stale on the very next build.
 *
 * STAGING IS NOT GUESSED. A staging binary that subscribed to production would
 * pull production JavaScript into a test build, so the prefix comes from
 * whatever the native side already knows; only when it knows nothing does this
 * fall back to production, which is what an App Store build is.
 */
async function subscribe() {
  const p = LU();
  if (!p?.setChannel) return null;
  // Already set by the native side - an xcconfig or a resValue - is the more
  // specific instruction, and it is the only thing that knows about staging.
  try {
    const existing = (await p.getChannel?.())?.channel;
    if (existing) return existing;
  } catch { /* the plugin is entitled to have no answer yet */ }

  const App = plugin('App');
  let build = '';
  try { build = String((await App?.getInfo?.())?.build ?? ''); } catch { build = ''; }
  if (!/^\d+$/.test(build)) return null;          // no build number, no guessing

  const channel = `production-${platform === 'android' ? 'android' : 'ios'}-${build}`;
  try { await p.setChannel({ channel }); return channel; } catch { return null; }
}

export function start({ isSafeToReload = () => true } = {}) {
  if (!isNative) return;
  markReady();

  // A reload that throws away what someone was typing is worse than an update
  // that waits until tomorrow. `main.js` passes `app.isIdle`, which is the same
  // predicate the web service worker asks — override that one method and both
  // update paths respect it. For a long time this parameter had a default and
  // no caller, so `docs/OTA.md` and `AppHost.isIdle`'s own docstring both
  // described a wiring that did not exist and every OTA reloaded on the spot.

  const tick = async () => {
    // Subscribing first, every tick: it is idempotent, it costs one plugin
    // call, and a binary updated to a new build number needs the new channel.
    await subscribe();
    const staged = await checkForUpdate();
    if (staged) await applyIfIdle(isSafeToReload);
  };

  tick();

  const App = plugin('App');
  try {
    App?.addListener?.('appStateChange', ({ isActive }) => { if (isActive) tick(); });
  } catch { /* no App plugin — boot-time check only */ }
}
